<script setup>
  import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
  import useLegacyBridge from "@/composables/useLegacyBridge";
  import { useForecastTableModelSelection } from '@/composables/useForecastTableModelSelection';
  import ModelSelector from '@/components/ModelSelector.vue';

  const model = defineModel();

  const props = defineProps({
    spotInfo: {
      type: Object,
      required: true
    },
    selectedModelId: {
      type: [String, Number],
      required: false
    },
    useSavedState: {
      type: Boolean,
      default: true
    },
    updateKey: {
      type: Number,
      default: () => Date.now
    },
    containerId: {
      type: [String, Number],
      required: false
    }
  });

  const { forecastTable } = useLegacyBridge();

  // The Vue ModelSelector owns model selection now; this drives the legacy
  // FxTable via the EventBus contract (mirrors the wind graph).
  const { selectedModelId, onModelsLoaded, onModelSelected } = useForecastTableModelSelection({
    spotId: () => props.spotInfo.spotId,
    containerId: () => props.containerId,
    initialSelection: () => props.selectedModelId,
  });

  const contentContainer = ref(null);
  const modelNameContainer = ref(null);
  const modelName = ref(null);
  
  function loadForecastTable() {
    // Tear down a previous instance's EventBus listener before replacing it.
    model.value?.destroy?.();

    const settings = {
      modelNameTarget: modelNameContainer.value,
      spotName: props.spotInfo.spotName,
      spotId: props.spotInfo.spotId,
      favSpotId: props.spotInfo.spotFavoriteId,
      lat: props.spotInfo.lat,
      lon: props.spotInfo.lon,
      mainRows: 'wind,gust,sky,temp,wave',
      moreRows: 'precip,cloud,pres,run',
      showBasicDetailedToggle: false,
      showDaily7DayToggle: false,
      dailyMode: 'detailed',
      sevenDayMode: 'basic',
      useExternalControls: true,
      // The Vue ModelSelector renders the selector and drives selection via the
      // EventBus; FxTable no longer builds the classic selector.
      useExternalModelSelector: true,
      useSavedState: props.useSavedState,
      setFixedWidth: false,
      containerId: props.containerId
    }

    model.value = forecastTable(contentContainer.value, settings);
  }

  const onForecastTableRendered = (e) => {
    if (!e.detail.modelDataSet) return;

    if (props.containerId) {
      if (e.detail.containerId != props.containerId) return;
    } else {
      if (e.detail.spotId != props.spotInfo.spotId) return;
    }

    modelName.value = e.detail.modelDataSet.model_name;
  };

  onMounted(() => {
    window.$EventBus.addEventListener('cosmos-forecast-table-rendered', onForecastTableRendered);
    loadForecastTable();
  })

  onBeforeUnmount(() => {
    window.$EventBus.removeEventListener('cosmos-forecast-table-rendered', onForecastTableRendered);
    model.value?.destroy?.();
  })

  watch(() => [props.updateKey, props.spotInfo], () => {
    // The watcher can fire while the table DOM isn't mounted (e.g. rapid map
    // spot-click / quick-view open-close), leaving the container refs null.
    if (!contentContainer.value) return

    contentContainer.value.innerHTML = "";
    modelNameContainer.value.innerHTML = "";
    loadForecastTable();
  })

</script>

<template>
  <div class="jwx-data-container__scrolling-submenus jw-model-selector-container">
    <ModelSelector
      :id="`forecast-table-model-selector-${props.containerId ?? props.spotInfo.spotId}`"
      :spotId="props.spotInfo.spotId"
      :favSpotId="props.spotInfo.spotFavoriteId"
      :lat="props.spotInfo.lat"
      :lon="props.spotInfo.lon"
      :analytics="{
        initiator: 'Forecast_Table',
        spotName: props.spotInfo.spotName,
        spotId: props.spotInfo.spotId,
        lat: props.spotInfo.lat,
        lon: props.spotInfo.lon
      }"
      v-model="selectedModelId"
      @models:loaded="onModelsLoaded"
      @input:change="onModelSelected"
    />
  </div>
  <div
    class="jwx-data-container__meta-info jw-model-selector-container"
    ref="modelNameContainer"
  >
    <p class="jwx-data-container__meta-text">{{ modelName }}</p>
</div>
  <div
    class="jwx-data-container__content"
    style="width: 100%;"
    ref="contentContainer"
  ></div>
</template>